<template>
    <div class="analytics-report-page">
        <header>
            <div class="left">
                <ds-icon-button
                    name="arrow-left"
                    @click="back"
                />

                <h3>{{ title }}</h3>
            </div>

            <div class="actions">
                <template v-if="filterOptions && visible">
                    <ds-text-button
                        v-if="activeFiltersCount === 0"
                        leading-icon="filter"
                        @click="filtersModalOpen = true"
                    >
                        Filters
                    </ds-text-button>

                    <ds-filled-button
                        v-else
                        leading-icon="filter"
                        @click="filtersModalOpen = true"
                    >
                        Filters ({{ activeFiltersCount }})
                    </ds-filled-button>
                </template>

                <a href="https://forms.gle/8ssebSZv1WNDbed87" target="_blank" class="feedback-link">
                    Feedback
                </a>
            </div>
        </header>

        <div v-if="!visible" class="spinner-container">
            <ds-spinner />
        </div>

        <div id="looker-container">
            <div id="looker-iframe-container">
                <iframe
                    id="looker-iframe"
                    width="100%"
                    height="100%"
                    :src="url"
                    frameborder="0"
                />
            </div>
        </div>

        <analytics-filters-modal
            v-if="filterOptions"
            v-model="filters"
            :options="filterOptions"
            :open="filtersModalOpen"
            @close="filtersModalOpen = false"
        />

        <ds-focus-on-keyboard-only />
    </div>
</template>

<script>
import AnalyticsFiltersModal from './AnalyticsFiltersModal';

import {
    CONTACT_GROWTH_FILFTER_OPTIONS,
    EMAIL_DELIVERABILITY_FILTER_OPTIONS,
    PAYMENTS_FILTER_OPTIONS,
} from './analytics.constants';

export default {
    components: {
        AnalyticsFiltersModal,
    },

    props: {
        reportId: Number,
        lookerResponse: Object,
        lookerDomain: String,
    },

    data() {
        return {
            filters: [],
            title: '',
            url: '',
            visible: false,
            filtersModalOpen: false,
        };
    },

    mounted() {
        window.addEventListener('message', this.handleLookerEvent, false);
        this.url = this.lookerResponse.url;
    },

    destroyed() {
        window.removeEventListener('message', this.handleLookerEvent);
    },

    watch: {
        filters: {
            handler() {
                this.updateFilters();
            },
            deep: true,
        },
    },

    computed: {
        activeFiltersCount() {
            return this.filters.filter(({ query }) => Boolean(query)).length;
        },

        filterOptions() {
            let options = null;

            if (this.reportId === 5 || this.reportId === 13) {
                options = CONTACT_GROWTH_FILFTER_OPTIONS;
            } else if (this.reportId === 3 || this.reportId === 14) {
                options = EMAIL_DELIVERABILITY_FILTER_OPTIONS;
            } else if (this.reportId === 6 || this.reportId === 15) {
                options = PAYMENTS_FILTER_OPTIONS;
            }

            return options;
        },

        lookerFilterRequest() {
            const request = {};

            this.filterOptions.forEach(({ key }) => {
                const filter = this.filters.find(({ key: filterKey }) => filterKey === key);

                if (filter && filter.key && filter.query) {
                    request[key] = filter.query;
                } else {
                    request[key] = '';
                }
            });

            return request;
        },
    },

    methods: {
        handleLookerEvent(event) {
            if (event && event.data && event.source === document.getElementById('looker-iframe').contentWindow) {
                if (event.origin.indexOf('keapanalytics.infusionsoft.com') > -1) {
                    const data = JSON.parse(event.data);

                    if (data.type === 'dashboard:run:complete') {
                        this.visible = true;
                    }

                    if (data.type === 'dashboard:loaded') {
                        this.title = data.dashboard.title;
                    }
                }
            }
        },

        updateFilters() {
            const iframe = document.getElementById('looker-iframe');

            const filtersRequest = JSON.stringify({
                type: 'dashboard:filters:update',
                filters: this.lookerFilterRequest,
            });

            iframe.contentWindow.postMessage(filtersRequest, this.lookerDomain);

            iframe.contentWindow.postMessage(JSON.stringify({
                type: 'dashboard:run',
            }), this.lookerDomain);
        },

        back() {
            window.history.back();
        },
    },
};
</script>

<style lang="scss" scoped>
.analytics-report-page {
    min-height: 100%;
}

#looker-container {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
}

#looker-iframe-container {
    height: 100%;
    width: 100%;
}

.feedback-link {
    line-height: 60px;
    color: $color-primary;
    font-size: 14px;
    font-weight: bold;
}

header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    padding: 0 $spacing-200;
    background-color: $color-white;
}

.left {
    display: flex;
    align-items: center;
    height: 60px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    h3 {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.actions {
    display: flex;
    align-items: center;

    a {
        @include margin-start($spacing-200);
    }
}

h3 {
    @include margin-start($spacing-100);

    @media($small) {
        display: none;
    }
}

iframe {
    border: none;
}
</style>
